import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import ProductCard from './ProductCard';
import { ArrowRight, Sparkles } from 'lucide-react';

interface ProductRecommendationsProps {
  currentProductId?: string;
  category?: string;
  title?: string;
  limit?: number;
}

const ProductRecommendations: React.FC<ProductRecommendationsProps> = ({
  currentProductId,
  category,
  title = "You might also like",
  limit = 4
}) => {
  const { products, loading } = useProducts({ 
    category,
    featured: true,
    limit: limit + 1 // Get one extra in case we need to filter out current product
  });

  // Filter out current product if provided
  const recommendedProducts = currentProductId 
    ? products.filter(p => p.id !== currentProductId).slice(0, limit)
    : products.slice(0, limit);

  if (loading || recommendedProducts.length === 0) {
    return null;
  }

  return (
    <section className="py-12 bg-gray-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-6 w-6 text-red-700" />
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover more amazing instruments handpicked just for you
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {recommendedProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link 
            to="/products"
            className="inline-flex items-center space-x-2 btn btn-outline py-3 px-6 hover:bg-red-700 hover:text-white transition-colors"
          >
            <span>View All Products</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductRecommendations;